import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { STALE_CLIENT_CODE, trpcErrorCode } from './trpc.ts';

/**
 * One QueryClient for the app. Defaults favour "always fresh enough" over cleverness:
 *  - refetch on window focus and on reconnect (live pings are a bonus, never relied on)
 *  - 30 s stale time so tab-switching doesn't hammer the API
 *  - no retries for 4xx-style tRPC errors (they won't fix themselves)
 */
export interface QueryClientHooks {
  /** Session expired / signed out elsewhere → send the user to the sign-in page. */
  onUnauthorized?: () => void;
  /** API says this build is too old → show the "Reload to update" prompt. */
  onStaleClient?: () => void;
}

const NO_RETRY_CODES = new Set([
  'BAD_REQUEST',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'PARSE_ERROR',
  'UNPROCESSABLE_CONTENT',
  STALE_CLIENT_CODE,
]);

export function shouldRetry(failureCount: number, error: unknown): boolean {
  const code = trpcErrorCode(error);
  if (code && NO_RETRY_CODES.has(code)) return false;
  return failureCount < 2;
}

export function createQueryClient(hooks: QueryClientHooks = {}): QueryClient {
  const onError = (error: unknown) => {
    const code = trpcErrorCode(error);
    if (code === 'UNAUTHORIZED') hooks.onUnauthorized?.();
    if (code === STALE_CLIENT_CODE) hooks.onStaleClient?.();
  };
  return new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 10 * 60_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: shouldRetry,
      },
      mutations: { retry: false },
    },
  });
}
